/* eslint-disable lines-between-class-members */
/* eslint-disable no-underscore-dangle */
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';

import { DataOptions } from 'vuetify';
import {
  User,
  Dataset,
  DatasetListing,
  DatasetListingResponse,
  ApplicationData,
  VideoResponse,
  VideoList,
  DatasetGenerationRequest,
  DatasetGenerationResponse,
  DatasetDeletionResponse,
  RegisterUserPayload,
  UpdateUserPayload,
  ChangeUserLevelPayload,
  ResetUserPasswordPayload,
  ResetPasswordPayload,
  AnnotationFrame,
  NewAnnotationFrame,
  EvaluateVideoPayload,
  EvaluateVideoDispatchResponse,
  Upload,
  Task,
  Institution,
  AnnotationFrameStatus,
  ChangeUserInstitutionsPayload,
  UserMetricsPeriod,
  UserMetrics,
  UserMetricsGranularity,
  UserMetricsTimeline,
  CheckResult,
  GenericResponse,
  PaginatedNotifications,
  Models,
  ModelStatisticsResponse,
  VideoEvaluationResult,
  VideoUploadPolicy,
  StatisticsResult,
} from '@/types';

import { sleep } from '@/utils';
import { LOGIN_COOKIE } from '@/environment';

// The time (1 minute in ms) before the token expires to refresh it
const REFRESH_BUFFER = 60 * 1000;
const ACCESS_TIMEOUT = 60 * 60 * 1000;
const HEARTBEAT_INTERVAL = 60 * 1000;
const REQUEST_TIMEOUT = 15 * 1000;
// const REFRESH_TIMOUT = 3 * 24 * 60 * 60 * 1000;
function getTimeout(): number | undefined {
  // Must multiply by 1000 since Date.now() uses ms
  return ACCESS_TIMEOUT - REFRESH_BUFFER;
}

const baseApiUrl = (user: boolean) => (user ? '/api/v1/user' : '/api/v1');

export default class Client {
  public axios: AxiosInstance;

  serverURL: string;
  user: User | null;
  refreshTimeoutId?: number;

  /**
   * Create and return a client instance. This is done in a helper function
   * to allow for asynchronous construction.
   * @param serverURL The base URL of the API server to use
   * @param token The access token (JWT) to use for each request
   * @returns The instantiated client.
   */
  static async Create(serverURL: string, refresh = false): Promise<Client> {
    const client = new Client(serverURL);
    await client.initAsync(refresh);

    return client;
  }

  constructor(serverURL: string) {
    this.axios = axios.create({ baseURL: serverURL, timeout: REQUEST_TIMEOUT });
    this.axios.defaults.withCredentials = true;
    this.serverURL = serverURL;
    this.user = null;

    /* Add network connection interceptors, respecting order */

    // Retry transient network failures a small, bounded number of times.
    axiosRetry(this.axios, {
      retries: 3,
      retryCondition: (err) => !err.response,
      retryDelay: (count) => Math.min(count * 1000, 5_000),
      shouldResetTimeout: true,
    });
  }

  public destroy() {
    this.serverURL = '';
    window.clearTimeout(this.refreshTimeoutId);
  }

  async initAsync(refresh = false) {
    if (refresh) {
      await this.refreshToken();
    }
    await this.fetchUser();
    // this.refreshTimeoutId = window.setTimeout(this.refreshToken.bind(this), getTimeout());

    // Init the low-frequency authentication heartbeat.
    this.heartbeat();
  }

  async heartbeat() {
    // eslint-disable-next-line no-constant-condition
    while (this.serverURL) {
      // Do not issue a duplicate request immediately after fetchUser(), and
      // avoid background-tab traffic entirely.
      // eslint-disable-next-line no-await-in-loop
      await sleep(HEARTBEAT_INTERVAL);
      if (!this.serverURL || document.visibilityState !== 'visible') {
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        await this.axios.get('/auth/whoAmI');
      } catch (e) {
        const error = e as AxiosError;
        if (error.response?.status === 401) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await this.refreshToken();
          } catch (_) {
            // The next heartbeat will retry. Do not show global connection alerts.
          }
        }
      }
    }
  }

  async refreshToken() {
    try {
      await this.axios.post('/auth/token/refresh');
      this.refreshTimeoutId = window.setTimeout(this.refreshToken.bind(this), getTimeout());
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        this.destroy();
        Cookies.remove(LOGIN_COOKIE);
        if (window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
      throw error;
    }
  }

  async logoutUser() {
    const resp = await this.axios.post('/auth/token/remove');
    const { logout: isLogout } = resp.data;
    return isLogout;
  }

  async fetchUser() {
    const { data } = await this.axios.get('/auth/whoAmI');
    if (data.status === 401) {
      await this.refreshToken();
      await this.fetchUser();
    } else {
      this.user = data;
    }
  }

  async sendEmailVerification(email?: string): Promise<string> {
    const { data } = await this.axios.post('/auth/email-verification', email ? { email } : {});
    return data;
  }

  // Requires Admin
  async fetchUsers(search = '', limit = 25): Promise<User[]> {
    const { data } = await this.axios.get('/auth/users', { params: { search, limit } });
    return data;
  }

  async getApplicationData(): Promise<ApplicationData> {
    const resp = await this.axios.get('/api/v1/application_data');
    return resp.data;
  }

  async getVideoUploadPolicy(): Promise<VideoUploadPolicy> {
    const { data } = await this.axios.get('/api/v1/upload-policy');
    return data;
  }

  async userMetricsTimeline(granularity: UserMetricsGranularity): Promise<UserMetricsTimeline> {
    const resp = await this.axios.get('/api/v1/metrics/user/timeline', { params: { granularity } });
    return resp.data;
  }

  async fetchInstitutions(): Promise<string[]> {
    const resp: AxiosResponse<Institution[]> = await this.axios.get('/api/v1/institutions');
    return resp.data.map((obj) => obj.name);
  }

  async stopTask(taskId: string): Promise<string> {
    const resp = await this.axios.delete(`api/v1/task/${taskId}`);
    return resp.data;
  }

  // Dataset Operations
  async getRemoteDatasets(options: DataOptions, search = '', filterStudyType = 'pain-mgs', filterSubjectType = '', filterValidationOnly = '', flagged = false): Promise<DatasetListingResponse> {
    let filter = {
      _id: { $regex: search, $options: 'i' },
      flagged: flagged ? { $gt: 0 } : { $gte: 0 },
    };
    const studyTypeFilter = { study_type: filterStudyType };
    filter = filterStudyType ? { ...filter, ...studyTypeFilter } : filter;
    const subjectTypeFilter = { subject_type: filterSubjectType.toLowerCase() };
    filter = filterSubjectType ? { ...filter, ...subjectTypeFilter } : filter;
    const validationOnlyFilter = { validation_only: filterValidationOnly };
    filter = (filterValidationOnly !== '' && filterValidationOnly !== null) ? {
      ...filter,
      ...validationOnlyFilter,
    } : filter;
    const params: Record<string, unknown> = {
      page: options.page,
      limit: options.itemsPerPage,
      filter,
    };
    // Add sorting params if necessary
    if (options.sortBy.length) {
      // eslint-disable-next-line prefer-destructuring
      params.sort_by = options.sortBy[0];
      params.order_by = options.sortDesc[0] ? 'desc' : 'asc';
    }

    const resp = await this.axios.get('/api/v1/dataset_meta', { params });
    return resp.data;
  }

  async getRemoteDataset(datasetId: string, user = true): Promise<Dataset> {
    const resp = await this.axios.get(`${baseApiUrl(user)}/dataset`, { params: { dataset_id: datasetId } });
    return resp.data;
  }

  async saveRemoteDataset(dataset: Dataset, user = true): Promise<boolean> {
    if (!dataset.length) { return false; }
    await this.axios.put(`${baseApiUrl(user)}/dataset`, dataset);

    return true;
  }

  async generateRemoteDataset(request: DatasetGenerationRequest): Promise<DatasetGenerationResponse> {
    const { video, params } = request;
    const resp = await this.axios.post(`/api/v1/videos/generate/${video}`, null, { params });

    return resp.data;
  }

  async exportUserDataset(userDatasetId: string, user = false): Promise<any> {
    const resp = await this.axios.post(`${baseApiUrl(user)}/dataset/import/${userDatasetId}`);
    return resp.data;
  }

  // Confirm / Unconfirm
  async confirmDataset(dataset: Dataset, datasetId: string, user = true): Promise<Dataset> {
    const { data } = await this.axios.put(`${baseApiUrl(user)}/dataset/confirm`, dataset);
    return data;
  }

  async unconfirmDataset(dataset: Dataset, datasetId: string, user = true): Promise<Dataset> {
    const { data } = await this.axios.put(`${baseApiUrl(user)}/dataset/pending`, dataset);
    return data;
  }

  async setDatasetItemStatus(item: AnnotationFrame, status: AnnotationFrameStatus, user = true): Promise<AnnotationFrame> {
    let fixedStatus: string = status;
    switch (status) {
      case 'confirmed':
        fixedStatus = 'confirm';
        break;
      case 'flagged':
        fixedStatus = 'flag';
        break;
      case 'pending':
        fixedStatus = item.status === 'flagged' ? 'unflag' : 'pending';
        break;
      default:
        break;
    }

    const { data } = await this.axios.put(`${baseApiUrl(user)}/dataset/item/${item._id}/${fixedStatus}`, item);
    return data;
  }

  async saveDatasetItem(item: AnnotationFrame, user = true): Promise<void> {
    await this.axios.put(`${baseApiUrl(user)}/dataset/item/${item._id}`, item);
  }

  async saveNewDatasetItem(item: NewAnnotationFrame, user = true): Promise<void> {
    await this.axios.put(`${baseApiUrl(user)}/dataset`, [item]);
  }

  async deleteDatasetItem(item: AnnotationFrame, user = true): Promise<DatasetDeletionResponse> {
    const { data } = await this.axios.delete(`${baseApiUrl(user)}/dataset/item/${item._id}`);
    return data;
  }

  // Requires Admin
  async deleteRemoteDataset(dataset: DatasetListing, user = false): Promise<DatasetDeletionResponse> {
    const { _id: dataset_id } = dataset;
    const resp = await this.axios.delete(`${baseApiUrl(user)}/dataset`, { params: { filter: { dataset_id } } });

    return resp.data;
  }

  async updateValidationOnlyRemoteDataset(dataset_id: string, validation_only: boolean, user = false): Promise<boolean> {
    await this.axios.patch(`${baseApiUrl(user)}/dataset`, { validation_only }, { params: { filter: { dataset_id } } });
    return true;
  }

  // Model Statistics Operations
  async getModelStatistics(subjectType: string, studyType: string, modelId: string): Promise<ModelStatisticsResponse> {
    const endpoint = `/api/v1/ml/statistics/models/${subjectType}/${studyType}/${modelId}`;
    const resp = await this.axios.get(endpoint);
    return resp.data;
  }

  // Video Operations
  getVideoFrameUrl(imagePath: string) {
    return `${this.serverURL}/api/v1/videos/${imagePath}`;
  }

  async getRemoteVideos(options: DataOptions, search = '', filterSubjectType = '', filterSubjectSex = '', filterDataSource = ''): Promise<VideoResponse> {
    const endpoint = '/api/v1/videosPagination';
    const normalizedSearch = search.trim();
    let filter = normalizedSearch
      ? { $or: [{ _id: { $regex: normalizedSearch, $options: 'i' } }, { details: { $in: [normalizedSearch] } }] }
      : {};
    const subjectTypeFilter = { subject_type: filterSubjectType };
    filter = filterSubjectType ? { ...filter, ...subjectTypeFilter } : filter;
    const subjectSexFilter = { subject_sex: filterSubjectSex };
    filter = filterSubjectSex ? { ...filter, ...subjectSexFilter } : filter;
    const dataSourceFilter = { data_source: filterDataSource };
    filter = filterDataSource ? { ...filter, ...dataSourceFilter } : filter;
    const params = {
      page: options.page,
      limit: options.itemsPerPage,
      filter,
      sort_by: undefined as string | undefined,
      order_by: undefined as string | undefined,
    };

    // Add sorting params if necessary
    if (options.sortBy.length) {
      // eslint-disable-next-line prefer-destructuring
      params.sort_by = options.sortBy[0];
      params.order_by = options.sortDesc[0] ? 'desc' : 'asc';
    }

    const resp = await this.axios.get(endpoint, { params });
    return resp.data;
  }

  async getVideoFrame(imagePath: string): Promise<File> {
    const url = this.getVideoFrameUrl(imagePath);
    const resp = await this.axios.get(url, { responseType: 'blob' });

    return new File([resp.data], imagePath, { type: 'image/jpeg' });
  }

  // Check that a video with this name doesn't already exist
  async videoNameAvailable(name: string): Promise<CheckResult> {
    const checkEndpoint = `/api/v1/videos/checkUpload/${name}`;
    return (await this.axios.get(checkEndpoint)).data;
  }

  async uploadVideo(upload: Upload): Promise<string | null> {
    // Check filename doesn't already exist, before doing full upload
    const filenameAvailable = await this.videoNameAvailable(upload.file.name);
    if (!filenameAvailable.check) {
      return filenameAvailable.reason;
    }

    // Filename doesn't exist, let's go
    const formData = new FormData();
    formData.append('file', upload.file, upload.file.name);
    formData.append('meta', JSON.stringify(upload.meta));

    try {
      // Need to use put here due to server config issues
      const resp = await this.axios.put(
        '/api/v1/videos/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: upload.onProgress,
          cancelToken: upload.cancelToken.token,
          // Video uploads can legitimately exceed the normal API timeout.
          timeout: 0,
          'axios-retry': { retries: 0 },
        },
      );
      if (resp.status === 401) {
        await this.axios.put(
          '/api/v1/videos/upload',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: upload.onProgress,
            cancelToken: upload.cancelToken.token,
            timeout: 0,
            'axios-retry': { retries: 0 },
          },
        );
      }
      return null;
    } catch (error) {
      if (axios.isCancel(error)) {
        return 'Upload Cancelled.';
      }

      // Throw other errors
      throw error;
    }
  }

  async updateVideoEntry(video: VideoList): Promise<boolean> {
    const { name, data_source, subject_type, subject_sex, details, remarks } = video;
    const endpoint = `/api/v1/videos/${name}`;
    try {
      await this.axios.put(endpoint, { data_source, subject_type, subject_sex, details, remarks });
    } catch (error) {
      // The API returns an error when the video no longer exists.
      return false;
    }
    return true;
  }

  async deleteVideoEntry(video: VideoList): Promise<AxiosResponse<GenericResponse>> {
    const { name } = video;
    const endpoint = `/api/v1/videos/${name}`;

    return this.axios.delete(endpoint);
  }

  // Statistics (CSV post analysis)
  async analyseCsv(results: VideoEvaluationResult[], options = {}): Promise<StatisticsResult> {
    const payload = {
      task_results: results,
      options,
    };
    const resp = await this.axios.post('/api/v1/statistics/basic', payload);
    return resp.data;
  }

  async datasetToRows(scores: AnnotationFrame[]): Promise<any> {
    const payload = { scores };
    const resp = await this.axios.post('/api/v1/statistics/rows-from-dataset', payload);
    return resp.data;
  }

  // Computation Operations
  async evaluateImage(image: File): Promise<AnnotationFrame> {
    const resp = await this.axios.post('/api/v1/ml/evaluateImage', image, { headers: { 'Content-Type': 'image/jpeg' } });
    return resp.data;
  }

  async evaluationPrecheck(payload: EvaluateVideoPayload): Promise<CheckResult> {
    const endpoint = 'api/v1/videos/checkEvaluate';
    return (await this.axios.post(endpoint, payload)).data;
  }

  async getModels(): Promise<Models> {
    const resp = await this.axios.get('api/v1/ml/models');
    return resp.data;
  }

  async evaluateVideo(payload: EvaluateVideoPayload): Promise<string> {
    const endpoint = 'api/v1/ml/evaluateVideo';
    const resp: EvaluateVideoDispatchResponse = (await this.axios.post(endpoint, payload)).data;
    return resp.task_id;
  }

  async evaluateMotion(payload: EvaluateVideoPayload): Promise<string> {
    const endpoint = 'api/v1/ml/evaluateMotion';
    const resp: EvaluateVideoDispatchResponse = (await this.axios.post(endpoint, payload)).data;
    return resp.task_id;
  }

  // TODO: Use axios.getUri once it's fixed
  getDownloadLink(filename: string): string {
    return `${this.serverURL}/api/v1/download/${filename}`;
  }

  // User Operations
  async changePassword(payload: ResetPasswordPayload): Promise<User | false> {
    const endpoint = '/auth/changePassword';
    const successRegex = /Success : (.+)/;
    let resp: AxiosResponse;

    try {
      resp = await this.axios.post(endpoint, payload);
    } catch (error) {
      // If failed, user already exists
      return false;
    }

    const match = successRegex.exec(resp.data);
    const stringUser = match?.[1];

    if (stringUser) {
      // Let parsing errors bubble up, as they shouldn't happen
      return JSON.parse(stringUser);
    }

    // This should never be reached
    throw new Error(`Unknown Response Received from ${endpoint}.`);
  }

  async fetchUserTasks(page = 1, limit = 15, search = ''): Promise<{ tasks: Task[]; total: number }> {
    const resp = await this.axios.get('/api/v1/taskmanager/monitor', {
      params: {
        filter: { requested_by: this.user!._id }, page, limit, search,
      },
    });
    const tasks = resp.data.items;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tasks.forEach((task: any) => {
      if (task.video_filename === undefined) {
        const filename = task.args?.[0]?.filename;
        // eslint-disable-next-line no-param-reassign
        task.video_filename = filename ? filename.split('/').pop() : 'Unknown video';
      }
      if (task.status === undefined) {
        // eslint-disable-next-line no-param-reassign
        task.status = { status: 'FAILURE', progress: 0 };
      }
    });
    return { tasks, total: resp.data.total };
  }

  async deleteTask(task: Task) {
    await this.axios.delete(`/api/v1/taskmanager/monitor/${task.task_id}`);
  }

  async cancelTask(task: Task) {
    await this.axios.post(`/api/v1/taskmanager/monitor/${task.task_id}/cancel`);
  }

  async retryTask(task: Task): Promise<Task> {
    return (await this.axios.post(`/api/v1/taskmanager/monitor/${task.task_id}/retry`)).data;
  }

  /** **************
   * ADMIN FUNCTIONS
   *************** */

  async registerUser(user: RegisterUserPayload): Promise<boolean> {
    try {
      await this.axios.post('/auth/registerUser', user);
    } catch (error) {
      // If failed, user already exists
      return false;
    }

    return true;
  }

  async changeUserLevel(user: ChangeUserLevelPayload): Promise<void> {
    // This endpoint doesn't return anything useful, so nothing returned
    await this.axios.post('/auth/changeUserLevel', user);
  }

  async updateUser(username: string, payload: UpdateUserPayload): Promise<void> {
    await this.axios.put(`/auth/users/${username}`, payload);
  }

  async removeUser(username: string): Promise<void> {
    await this.axios.post('/auth/removeUser', { username });
  }

  async changeUserInstitutions(payload: ChangeUserInstitutionsPayload): Promise<void> {
    // This endpoint doesn't return anything useful, so nothing returned
    await this.axios.post('/auth/changeUserAccessibleInstitutions', payload);
  }

  async resetUserPassword(user: ResetUserPasswordPayload): Promise<void> {
    // This endpoint doesn't return anything useful, so nothing returned
    await this.axios.post('/auth/resetPassword', user);
  }

  async userMetrics(period: UserMetricsPeriod = 'from_inception'): Promise<UserMetrics> {
    const resp = await this.axios.get('/api/v1/metrics/user', { params: { period } });
    return resp.data;
  }

  async submitDuplicateSamples(datasetPrefix: string, samples: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    // Make request
    await this.axios.post('/api/v1/duplicate_samples', formData, {
      params: {
        dataset_prefix: datasetPrefix,
        samples_per_dataset: samples,
      },
    });
  }

  async fetchNotifications(page: number, limit: number, search?: string): Promise<PaginatedNotifications> {
    const filter: Record<string, unknown> = { type: 'selfRegister' };
    if (search) {
      filter.text = { $regex: search, $options: 'i' };
    }

    const resp = await this.axios.get('/auth/notifications', { params: { page, limit, filter } });
    return resp.data;
  }
}
